import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Users, AlertTriangle, Store, CalendarClock, UserX } from "lucide-react";
import VendorAssignmentModal from "./VendorAssignmentModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const prettyStatus = (s) => (s || "pending").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const IssueList = ({ title, items, icon: Icon, tint, onOpen, testid }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-3.5" data-testid={testid}>
    <p className="text-[11px] font-bold text-[#64748B] uppercase tracking-wide mb-2 flex items-center gap-1.5">
      <Icon size={13} style={{ color: tint }} /> {title} <span className="ml-auto text-[#1E1B4B]">{items.length}</span>
    </p>
    {items.length === 0 ? (
      <p className="text-[11px] text-emerald-600 font-semibold">All clear</p>
    ) : (
      <ul className="space-y-1 max-h-44 overflow-y-auto">
        {items.map((b) => (
          <li key={b.id}>
            <button onClick={() => onOpen(b.id)} data-testid={`issue-open-${b.id}`}
              className="w-full text-left text-[11px] text-[#475569] hover:text-[#7C3AED] flex items-center justify-between gap-2 py-0.5">
              <span className="truncate">{b.customer_name} · {String(b.date).slice(0, 10)} · {b.pincode || "—"}</span>
              <span className="text-[9px] text-[#94a3b8] flex-shrink-0">{prettyStatus(b.vendor_assignment_status)}</span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const VendorWorkloadPanel = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openBooking, setOpenBooking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/scheduler/vendors/ops-workload`);
      setData(r.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={22} /></div>;
  if (!data) return <p className="text-xs text-center text-[#94a3b8] py-10">Failed to load workload</p>;

  return (
    <div className="space-y-4" data-testid="vendor-workload-panel">
      {/* Operational issue summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <IssueList testid="issue-no-ops" title="No OPS Assignment" items={data.issues.no_ops_assignment} icon={UserX} tint="#F59E0B" onOpen={setOpenBooking} />
        <IssueList testid="issue-pending-mandatory" title="Pending Mandatory Vendor" items={data.issues.pending_mandatory_vendor} icon={Store} tint="#3B82F6" onOpen={setOpenBooking} />
        <IssueList testid="issue-no-vendor" title="No Eligible Vendor" items={data.issues.no_vendor_available} icon={AlertTriangle} tint="#EF4444" onOpen={setOpenBooking} />
        <IssueList testid="issue-upcoming" title="Upcoming w/ Issues" items={data.issues.upcoming_with_issues} icon={CalendarClock} tint="#7C3AED" onOpen={setOpenBooking} />
      </div>

      {/* Per-member workload */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="member-workload">
        <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Users size={14} className="text-[#3B82F6]" /> Team Workload</h3>
        {data.workload.length === 0 ? (
          <p className="text-xs text-[#94a3b8] text-center py-4">No active OPS members.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {data.workload.map((w) => (
              <div key={w.phone} className={`p-3 rounded-xl border ${w.needs_action > 0 ? "border-amber-200 bg-amber-50/40" : "border-gray-100"}`} data-testid={`workload-${w.phone}`}>
                <p className="text-xs font-bold text-[#1E1B4B]">{w.name} <span className="text-[9px] text-[#94a3b8] font-medium">{w.role}</span></p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#475569]">
                  <span><b className="text-[#1E1B4B]">{w.assigned_bookings}</b> assigned</span>
                  <span><b className="text-[#1E1B4B]">{w.upcoming_events}</b> upcoming</span>
                  <span className={w.needs_action > 0 ? "text-amber-600 font-bold" : ""}><b>{w.needs_action}</b> need action</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {openBooking && (
        <VendorAssignmentModal bookingId={openBooking} authAxios={authAxios}
          onClose={() => setOpenBooking(null)} onChanged={load} />
      )}
    </div>
  );
};

export default VendorWorkloadPanel;
